import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProductSelection from './ProductSelection';
import AIInfoScreen from './AIInfoScreen';
import ClientContactSelection from './ClientContactSelection';
import QuotationBuilder from './QuotationBuilder';
import QuotationPreview from './QuotationPreview';
import DemoGuide from './DemoGuide';
import api from '../utils/axios';
import './QuotationWizard.css';

const QuotationWizard = () => {
  const { user, selectedSubscriberId, isLicenseActive, isTrialExpired } = useAuth();
  const location = useLocation();
  const applyOfferRequestId = location.state?.applyOfferRequestId;
  const loadQuotationId = location.state?.loadQuotationId;
  const [currentStep, setCurrentStep] = useState(1);
  const [success, setSuccess] = useState(null);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [wizardData, setWizardData] = useState({
    selectedProducts: [],
    query: '',
    clientName: '',
    clientData: '',
    selectedCompany: null,
    selectedContact: null,
    aiResponse: null,
    quotationData: null,
    quotationId: null,
    draftQuotationData: null,
    draftProducts: null,
    subscriberId: null
  });
  const [fieldLabels, setFieldLabels] = useState(null);
  const [fieldLabelsEn, setFieldLabelsEn] = useState(null);
  const [defaultPaymentSettings, setDefaultPaymentSettings] = useState(null);
  const [standaardinhoudTemplates, setStandaardinhoudTemplates] = useState([]);
  const [subscriberCompanyName, setSubscriberCompanyName] = useState('');
  const [subscriberPersoon, setSubscriberPersoon] = useState('');

  const navigate = useNavigate();
  const activeStepRef = React.useRef(null);

  // Reset wizard when the selected company changes
  const prevSubscriberRef = React.useRef(selectedSubscriberId);
  useEffect(() => {
    if (prevSubscriberRef.current && selectedSubscriberId && prevSubscriberRef.current !== selectedSubscriberId) {
      // Company changed — reset wizard to step 1 with empty data
      setWizardData({
        selectedProducts: [],
        query: '',
        clientName: '',
        clientData: '',
        selectedCompany: null,
        selectedContact: null,
        aiResponse: null,
        quotationData: null,
        quotationId: null,
        draftQuotationData: null,
        draftProducts: null,
        subscriberId: null
      });
      setCurrentStep(1);
      setSuccess(null);
    }
    prevSubscriberRef.current = selectedSubscriberId;
  }, [selectedSubscriberId]);

  useEffect(() => {
    const sid = selectedSubscriberId || user?.subscriberId;
    if (!sid) return;
    api.get(`/api/subscribers/${sid}`).then((res) => {
      if (res.data?.quotation_field_labels) {
        setFieldLabels(res.data.quotation_field_labels);
      }
      if (res.data?.quotation_field_labels_en) {
        setFieldLabelsEn(res.data.quotation_field_labels_en);
      }
      const cd = res.data?.company_details || {};
      setSubscriberCompanyName(res.data?.company_name || '');
      setSubscriberPersoon(cd?.persoon || '');
      if (cd.default_payment_prepayment != null && cd.default_payment_delivery != null) {
        setDefaultPaymentSettings({
          prepayment: cd.default_payment_prepayment,
          delivery: cd.default_payment_delivery
        });
      } else {
        setDefaultPaymentSettings(null);
      }
      if (Array.isArray(cd.standaardinhoud_templates) && cd.standaardinhoud_templates.length > 0) {
        setStandaardinhoudTemplates(cd.standaardinhoud_templates);
      } else if (cd.standaardinhoud) {
        setStandaardinhoudTemplates([{ name: 'Standaard', content: cd.standaardinhoud }]);
      } else {
        setStandaardinhoudTemplates([]);
      }
    }).catch(() => {});
  }, [selectedSubscriberId, user?.subscriberId]);

  // Laad een bestaande offerte als loadQuotationId is meegegeven
  useEffect(() => {
    if (!loadQuotationId) return;
    setLoadingQuotation(true);
    api.get(`/api/quotations/${loadQuotationId}`, {
      headers: { 'X-Subscriber-Id': selectedSubscriberId || '' }
    }).then((res) => {
      const q = res.data;
      if (!q) return;
      const extra = q.quotation_extra || {};
      // Map DB data terug naar draftQuotationData (QuotationBuilder shape)
      const draftData = {
        title: q.title || '',
        clientName: q.client_name || '',
        clientData: q.client_data || '',
        description: q.description || '',
        greeting: q.greeting || '',
        reference: q.reference || '',
        badge: q.badge || '',
        highlight: extra.highlight || '',
        closing: extra.closing || '',
        companyContact: extra.company_contact || '',
        validUntil: extra.valid_until || '',
        standaardinhoud: extra.standaardinhoud || '',
        standaardinhoudTemplate: extra.standaardinhoud_template || '',
        offerToelichting: extra.offer_toelichting || '',
        aiQuery: extra.ai_query || '',
        language: extra.language || 'nl',
        pricing: {
          total: q.total_price ?? 0,
          subtitle: extra.price_subtitle || 'excl. btw',
          paymentTerms: extra.payment_terms || '',
          paymentDescription: extra.payment_description || '',
          showOneTime: extra.show_one_time !== false,
          oneTimeAmount: extra.one_time_amount ?? q.total_price ?? 0,
          oneTimeAmountManual: !!extra.one_time_amount_manual,
          showMonthly: !!extra.show_monthly,
          monthlyAmount: extra.monthly_amount ?? 0
        }
      };
      // Map quotation_products terug naar products array
      // Gebruik name/description uit quotation_products (aangepast door gebruiker), fallback naar products tabel
      // Let op: ?? ipv || zodat een opgeslagen lege string ("") NIET terugvalt naar het product-sjabloon
      const isPriceRange = (d) => d && /^\s*€\s*[\d.,]+\s*[–—\-]\s*€\s*[\d.,]+\s*$/.test(d.trim());
      const isOffertebijlage = (name) => /^Offertebijlage\b/i.test(name);
      const prods = (q.quotation_products || [])
        .filter(qp => {
          const name = (qp.name != null ? qp.name : null) ?? qp.products?.name ?? '';
          return !isOffertebijlage(name);
        })
        .map((qp, idx) => {
          const rawName = (qp.name != null ? qp.name : null) ?? qp.products?.name ?? '';
          const rawDesc = (qp.description != null ? qp.description : null) ?? qp.products?.description ?? '';
          return {
            id: qp.product_id,
            _uid: `${qp.product_id || 'prod'}-${idx}-${Date.now()}`,
            name: rawName || 'Product',
            description: isPriceRange(rawDesc) ? '' : rawDesc,
            price: qp.price ?? qp.products?.price ?? 0,
            quantity: qp.quantity ?? 1
          };
        });
      setWizardData(prev => ({
        ...prev,
        draftQuotationData: draftData,
        draftProducts: prods,
        selectedProducts: prods,
        clientName: q.client_name || '',
        clientData: q.client_data || '',
        selectedCompany: q.client_odoo_partner_id ? { odoo_partner_id: q.client_odoo_partner_id, name: q.client_name } : null,
        query: extra.ai_query || '',
        subscriberId: q.subscriber_id || selectedSubscriberId,
        quotationId: loadQuotationId,
        isLoadedQuotation: true,
        odooOpportunityId: q.odoo_opportunity_id || null,
        odooQuotationId: q.odoo_quotation_id || null
      }));
      setCurrentStep(5);
      // Wis de route-state zodat refresh niet opnieuw laadt
      navigate(location.pathname, { replace: true, state: {} });
    }).catch((err) => {
      console.error('Load quotation error:', err);
    }).finally(() => {
      setLoadingQuotation(false);
    });
  }, [loadQuotationId]);

  // Op mobiel: actieve stap in beeld scrollen
  useEffect(() => {
    if (activeStepRef.current && window.innerWidth <= 768) {
      activeStepRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [currentStep]);

  // Blokkeer voorbij stap 4 als er geen actieve licentie is en trial verlopen
  const licenseBlocked = !isLicenseActive && isTrialExpired;

  const handleStepComplete = (step, data) => {
    if (licenseBlocked && step >= 4) return; // Blokkeer voorbij stap 4
    setWizardData(prev => ({ ...prev, ...data }));
    setCurrentStep(step + 1);
    if (step === 1 && applyOfferRequestId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  const steps = [
    { number: 1, title: 'Producten', component: ProductSelection },
    { number: 2, title: 'Informatie', component: AIInfoScreen },
    { number: 3, title: 'Klant & Contact', component: ClientContactSelection },
    { number: 4, title: 'Offerte Invullen', component: QuotationBuilder },
    { number: 5, title: 'Voorbeeld', component: QuotationPreview },
    { number: 6, title: 'PDF & Afronden', component: null },
    { number: 7, title: 'Gereed', component: null }
  ];

  // Bepaal minimale stap: voor geladen offertes niet verder terug dan stap 2
  const minStep = wizardData.isLoadedQuotation ? 2 : 1;

  const goBack = () => {
    if (currentStep > minStep) setCurrentStep(currentStep - 1);
  };

  // Klik op een stap: terug naar eerdere stap, of vervolg naar volgende als we quotationId hebben (stap 6+)
  const handleStepClick = (stepNumber) => {
    if (licenseBlocked && stepNumber > 4) return; // Blokkeer voorbij stap 4
    if (wizardData.isLoadedQuotation && stepNumber < 2) return; // Geladen offerte: niet voor stap 2
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber);
      return;
    }
    // Vooruit: alleen naar stap 6+ (PDF & Afronden) als we quotationId hebben
    if (stepNumber >= 6 && !wizardData.quotationId) return;
    if (stepNumber === currentStep + 1) {
      setCurrentStep(stepNumber);
    } else if (stepNumber > currentStep && stepNumber <= 7 && wizardData.quotationId) {
      setCurrentStep(stepNumber);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProductSelection
            initialOfferRequestId={applyOfferRequestId}
            initialSelectedProducts={wizardData.selectedProducts}
            onProductsSelected={(products, offerRequestMeta) => 
              handleStepComplete(1, {
                selectedProducts: products,
                selectedOfferRequest: offerRequestMeta || null,
                query: offerRequestMeta?.opmerkingen || ''
              })
            }
            onBack={goBack}
          />
        );
      
      case 2:
        return (
          <AIInfoScreen
            initialQuery={wizardData.query}
            onDataReceived={(data) => 
              handleStepComplete(2, { query: data.query, aiResponse: data })
            }
            onSkip={() => handleStepComplete(2, { query: '', aiResponse: null })}
            onBack={goBack}
          />
        );
      
      case 3:
        return (
          <ClientContactSelection
            initialClientName={wizardData.clientName}
            initialCustomerFromRequest={wizardData.selectedOfferRequest?.customer}
            onComplete={(data) => handleStepComplete(3, {
              selectedCompany: data.selectedCompany,
              selectedContact: data.selectedContact,
              clientName: data.clientName,
              clientData: data.clientData
            })}
            onBack={goBack}
          />
        );
      
      case 4:
        return (
          <QuotationBuilder
            aiData={wizardData.aiResponse}
            selectedProducts={wizardData.selectedProducts}
            clientName={wizardData.clientName}
            clientData={wizardData.clientData}
            selectedCompany={wizardData.selectedCompany}
            selectedContact={wizardData.selectedContact}
            subscriberCompanyName={subscriberCompanyName}
            subscriberPersoon={subscriberPersoon}
            prefillFromAI={true}
            draftQuotationData={wizardData.draftQuotationData}
            draftProducts={wizardData.draftProducts}
            standaardinhoudTemplates={standaardinhoudTemplates}
            defaultPaymentSettings={defaultPaymentSettings}
            existingQuotationId={wizardData.quotationId}
            onComplete={(data) => {
              if (licenseBlocked) return; // Geen licentie: blokkeer voorbij stap 4
              if (data.step === 'preview' && data.quotationData) {
                setWizardData(prev => ({
                  ...prev,
                  draftQuotationData: { ...data.quotationData, aiQuery: prev.query || '' },
                  draftProducts: data.products || [],
                  subscriberId: selectedSubscriberId || prev.subscriberId,
                  quotationId: data.quotationId || prev.quotationId
                }));
                setCurrentStep(5);
              } else if (data.step === 'pdf_generated') {
                handleStepComplete(4, { quotationId: data.quotationId });
              }
            }}
            onBack={goBack}
          />
        );
      
      case 5:
        return (
          <QuotationPreview
            quotationData={wizardData.draftQuotationData || {}}
            products={wizardData.draftProducts || []}
            fieldLabels={fieldLabels}
            fieldLabelsEn={fieldLabelsEn}
            subscriberId={wizardData.subscriberId || selectedSubscriberId}
            selectedCustomer={wizardData.selectedCompany}
            existingQuotationId={wizardData.quotationId}
            onBack={() => setCurrentStep(4)}
            onSaved={(quotationId) => setWizardData(prev => ({ ...prev, quotationId }))}
            onSaveAndContinue={(quotationId) => handleStepComplete(5, { quotationId })}
            onSkipToNext={() => setCurrentStep(6)}
          />
        );
      
      case 6:
        return <PDFGenerationStep
          quotationId={wizardData.quotationId}
          draftQuotationData={wizardData.draftQuotationData}
          draftProducts={wizardData.draftProducts}
          subscriberId={selectedSubscriberId}
          isDemo={user?.isDemo}
          werkUrl={user?.werk_url}
          existingOdooQuotationId={wizardData.odooQuotationId}
          existingOdooOpportunityId={wizardData.odooOpportunityId}
          onComplete={(data) => handleStepComplete(6, data)}
          onBack={goBack}
        />;
      
      case 7:
        return <CompletionStep
          usedExisting={wizardData.usedExisting}
          onComplete={() => {
            console.log('Navigating to home...');
            window.location.href = '/';
          }}
        />;
      
      default:
        return <div>Step {currentStep}</div>;
    }
  };

  return (
    <div className="quotation-wizard">
      {user?.isDemo && <DemoGuide currentStep={currentStep} />}
      <div className="wizard-progress">
        <div className="progress-mobile-caption" aria-hidden="true">
          {currentStep === 7 ? 'Gereed' : `${steps.find(s => s.number === currentStep)?.title || ''}`}
        </div>
        <div className="progress-mobile-step-badge" aria-live="polite">
          {currentStep === 7 ? 'Gereed' : `Stap ${currentStep} van 6`}
        </div>
        <div className="progress-bar">
          {steps.map((step) => {
            const isCompleted = currentStep > step.number;
            const isActive = currentStep === step.number;
            const canGoForward = step.number >= 6 ? !!wizardData.quotationId : true;
            const blockedByLicense = licenseBlocked && step.number > 4;
            const blockedByLoaded = wizardData.isLoadedQuotation && step.number < 2;
            const isClickable = !blockedByLicense && !blockedByLoaded && (isCompleted || (isActive && step.number < 7) || (step.number === currentStep + 1 && (step.number < 6 || canGoForward)));
            return (
              <div
                key={step.number}
                ref={isActive ? activeStepRef : undefined}
                className={`progress-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isClickable ? 'clickable' : ''} ${step.number === 7 ? 'final-step' : ''} ${blockedByLicense ? 'license-blocked' : ''} ${blockedByLoaded ? 'loaded-disabled' : ''}`}
                onClick={isClickable ? () => handleStepClick(step.number) : undefined}
                role={isClickable ? 'button' : undefined}
                title={isClickable ? (isCompleted ? `Ga naar ${step.title}` : step.number === currentStep + 1 ? `Vervolg: ${step.title}` : step.title) : ''}
              >
                {step.number !== 7 && <div className="step-number">{step.number}</div>}
                <div className="step-title">{step.title}</div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="wizard-content">
        {loadingQuotation && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
            Offerte laden...
          </div>
        )}
        {licenseBlocked && currentStep === 4 && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
            padding: '14px 18px', marginBottom: 16, color: '#991b1b', fontSize: 14
          }}>
            <strong>Licentie vereist.</strong> Uw proefperiode is verlopen. Activeer een licentiecode om de offerte af te ronden en een PDF te genereren.
            U kunt een licentieverzoek indienen via <a href="/instellingen" style={{ color: '#1B3A6B', fontWeight: 600 }}>Instellingen</a>.
          </div>
        )}
        {success && <div className="success">{success}</div>}
        {currentStep > 1 && (
          <button type="button" className="btn btn-secondary wizard-back" onClick={goBack}>
            ← Terug
          </button>
        )}
        {renderStep()}
      </div>
    </div>
  );
};

// PDF Generation Step
const PDFGenerationStep = ({ quotationId, draftQuotationData, draftProducts, subscriberId, isDemo, werkUrl, existingOdooQuotationId, existingOdooOpportunityId, onComplete, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [offertebijlageLoading, setOffertebijlageLoading] = useState(false);
  const [offertebijlageResult, setOffertebijlageResult] = useState(null);
  const [resolvedProductUrl, setResolvedProductUrl] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState(null);

  const canGeneratePdf = isDemo ? (draftQuotationData && draftProducts && draftProducts.length > 0) : !!quotationId;

  const generatePDF = async () => {
    if (isDemo) {
      if (!draftQuotationData || !draftProducts || draftProducts.length === 0) {
        setError('Ga terug naar de vorige stap en vul de offerte in.');
        return;
      }
    } else if (!quotationId) {
      setError('Geen offerte-ID beschikbaar. Ga terug en sla de offerte eerst op.');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusMsg('Verbinden met server...');
    const t0 = Date.now();
    try {
      setStatusMsg('PDF wordt gegenereerd...');
      let response;
      if (isDemo) {
        const sid = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedSubscriberId') : null;
        response = await api.post(
          '/api/demo/quotations/generate-pdf',
          {
            quotationData: draftQuotationData,
            products: draftProducts,
            subscriberId: sid || subscriberId
          },
          { responseType: 'blob', timeout: 60000 }
        );
      } else {
        const draft = draftQuotationData || {};
        response = await api.post(
          `/api/quotations/${quotationId}/generate-pdf`,
          {
            companyContact: draft.companyContact ?? '',
            highlight: draft.highlight,
            closing: draft.closing,
            validUntil: draft.validUntil,
            standaardinhoud: draft.standaardinhoud,
            offer_toelichting: draft.offerToelichting,
            price_subtitle: draft.pricing?.subtitle,
            language: draft.language || 'nl',
            payment_terms: draft.pricing?.paymentTerms,
            payment_description: draft.pricing?.paymentDescription,
            show_one_time: draft.pricing?.showOneTime,
            one_time_amount: draft.pricing?.oneTimeAmount,
            show_monthly: draft.pricing?.showMonthly,
            monthly_amount: draft.pricing?.monthlyAmount
          },
          { responseType: 'blob', timeout: 60000 }
        );
      }
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      setStatusMsg(`Antwoord ontvangen (${elapsed}s), bestand verwerken...`);

      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        try {
          const json = JSON.parse(text);
          setError(json.message || json.error || 'PDF genereren mislukt.');
        } catch (_) {
          setError(text || 'PDF genereren mislukt.');
        }
        setStatusMsg('');
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `offerte-${(draftQuotationData?.reference || quotationId || 'demo').replace(/[^a-zA-Z0-9-_]/g, '')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setPdfGenerated(true);
      setStatusMsg('');
    } catch (err) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      console.error(`PDF error after ${elapsed}s:`, err);
      let msg = 'PDF genereren mislukt.';
      if (err.code === 'ECONNABORTED') {
        msg = `PDF-generatie duurde te lang (${elapsed}s). Probeer het opnieuw.`;
      } else if (err.response) {
        const status = err.response.status;
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            msg = json.message || json.error || `Server fout (${status})`;
          } catch (_) {
            msg = `Server fout (${status})`;
          }
        } else if (err.response.data) {
          msg = err.response.data.message || err.response.data.error || `Server fout (${status})`;
        } else {
          msg = `Server fout (${status})`;
        }
      } else if (err.request) {
        msg = `Geen antwoord van server na ${elapsed}s. Is de server bereikbaar?`;
      } else {
        msg = err.message || msg;
      }
      setError(msg);
      setStatusMsg('');
    } finally {
      setLoading(false);
    }
  };

  const buildOdooProductUrl = (baseUrl, productId) => {
    if (!baseUrl || productId == null) return null;
    const base = String(baseUrl).trim().replace(/\/$/, '');
    return `${base}/odoo/products/${productId}`;
  };

  const createOffertebijlage = async () => {
    if (!quotationId) {
      setOffertebijlageResult({ success: false, error: 'Geen offerte geselecteerd.' });
      return;
    }
    setOffertebijlageLoading(true);
    setOffertebijlageResult(null);
    setResolvedProductUrl(null);
    try {
      const res = await api.post(`/api/quotations/${quotationId}/create-offertebijlage`);
      const data = res.data;
      console.log('create-offertebijlage response ontvangen:', JSON.stringify(data, null, 2));
      if (!data || typeof data !== 'object') {
        setOffertebijlageResult({ success: false, error: 'Onverwachte serverresponse.' });
        return;
      }
      if (data.success && data.odooProductId != null) {
        const productId = data.odooProductId ?? data.odoo_product_id;
        let base = data.odooBaseUrl ?? data.odoo_base_url;
        let url = data.productPageUrl ?? data.link ?? data.odooProductUrl ?? data.odoo_product_url ?? (base ? buildOdooProductUrl(base, productId) : null);
        if (!url && productId != null) {
          try {
            const settingsRes = await api.get('/api/settings');
            const odooUrl = settingsRes.data?.odoo?.url;
            if (odooUrl) {
              base = String(odooUrl).trim().replace(/\/$/, '');
              url = buildOdooProductUrl(base, productId);
            }
          } catch (settErr) {
            console.error('Settings ophalen mislukt:', settErr);
          }
        }
        const finalResult = {
          ...data,
          odooProductId: productId,
          odooBaseUrl: base,
          link: url,
          odooProductUrl: url,
          productPageUrl: url
        };
        console.log('Final link URL:', url);
        console.log('Setting offertebijlageResult:', JSON.stringify(finalResult, null, 2));
        setResolvedProductUrl(url || null);
        setOffertebijlageResult(finalResult);
      } else {
        setOffertebijlageResult(data);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Mislukt';
      setOffertebijlageResult({ success: false, error: errMsg });
    } finally {
      setOffertebijlageLoading(false);
    }
  };

  if (!canGeneratePdf && !isDemo) {
    return (
      <div className="wizard-step">
        <div className="container">
          <h2>Stap 6: PDF Genereren</h2>
          <div className="card">
            <p>Eerst de offerte opslaan op de vorige stap (Voorbeeld). Klik daar op <strong>✓ Opslaan</strong>, daarna kun je hier verder.</p>
            <p><strong>Tip:</strong> Selecteer bovenaan een <strong>Bedrijf</strong> als opslaan nog niet lukt.</p>
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              ← Terug naar Voorbeeld
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="container">
        <h2>Stap 6: PDF Genereren</h2>
        <div className="card">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {statusMsg && <div style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>{statusMsg}</div>}
          {pdfGenerated && <div className="success" style={{ marginBottom: 16 }}>PDF gedownload</div>}
          <button
            className="btn btn-primary"
            data-demo-step="demo-step-6"
            onClick={generatePDF}
            disabled={loading}
          >
            {loading ? 'Genereren...' : isDemo ? 'PDF Genereren (uit weergave stap 5)' : '1. PDF Genereren'}
          </button>

          {isDemo ? (
            <div className="demo-odoo-images" style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16 }}>Odoo-koppeling (demo)</h3>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
                In de echte app wordt de offerte-PDF als bijlage in Odoo opgeslagen. Hier tonen we hoe dat er in Odoo uitziet.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 800 }}>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 8 }}>Productpagina met PDF-bijlage (Offertebijlage)</p>
                  <img src="/demo/odoo-product-offertebijlage.png" alt="Odoo productpagina met Offertebijlage: product waar de PDF-bijlage in zit" style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 8 }}>Document zichtbaarheid: &quot;Binnen de pdf van de offerte&quot;</p>
                  <img src="/demo/document-binnen-offerte.png" alt="Document in Odoo met zichtbaarheid Binnen de pdf van de offerte" style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                </div>
                <div>
                  <p style={{ fontWeight: 600, marginBottom: 8 }}>Offertebouwer: PDF-bijlage in de offerte</p>
                  <img src="/demo/odoo-offertebouwer.png" alt="Odoo Offertebouwer-tab met offerte-PDF als bijlage" style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                </div>
              </div>
              {pdfGenerated && (
                <div style={{ marginTop: 20 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        await api.post('/api/demo/track', { event: 'completed' });
                      } catch (_) {}
                      onComplete({ synced: true });
                    }}
                  >
                    Demo afronden
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
          <hr style={{ margin: '20px 0', borderColor: '#e5e7eb' }} />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={createOffertebijlage}
            disabled={offertebijlageLoading || !pdfGenerated}
          >
            {offertebijlageLoading ? 'Bezig met Offertebijlage-product in Odoo...' : '2. Offertebijlage-product aanmaken/koppelen in Odoo'}
          </button>
          {!pdfGenerated && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>
              Genereer eerst de PDF (knop 1) voordat je de offertebijlage kunt aanmaken.
            </div>
          )}
          {offertebijlageResult && (
            <div style={{ marginTop: 16 }}>
              {offertebijlageResult.success ? (
                <div className="card" style={{ background: '#ecfdf5', border: '1px solid #10b981', padding: 16 }}>
                  {offertebijlageResult.pdfUploadDelegatedToGenerateStep ? (
                    <p style={{ margin: 0, fontSize: 14, color: '#065f46', fontWeight: 600, lineHeight: 1.5 }}>
                      {offertebijlageResult.message || 'Offertebijlage-product is klaar. De PDF wordt alleen bij PDF genereren (knop 1 op deze pagina) naar Odoo geüpload — één keer per generatie.'}
                    </p>
                  ) : offertebijlageResult.pdfUploaded ? (
                    <p style={{ margin: 0, fontSize: 14, color: '#065f46', fontWeight: 600 }}>
                      Product aangemaakt en PDF geüpload in Odoo.
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: 14, color: '#92400e' }}>
                      Product aangemaakt, maar PDF upload mislukt: {offertebijlageResult.pdfUploadError || 'onbekend'}
                    </p>
                  )}
                  {(() => {
                    const productId = offertebijlageResult.odooProductId ?? offertebijlageResult.odoo_product_id;
                    const base = offertebijlageResult.odooBaseUrl != null ? String(offertebijlageResult.odooBaseUrl).trim().replace(/\/$/, '') : null;
                    const odooUrl = offertebijlageResult.productPageUrl || offertebijlageResult.link || offertebijlageResult.odooProductUrl || resolvedProductUrl || (base && productId != null ? `${base}/odoo/products/${productId}` : null);
                    const werkProductUrl = offertebijlageResult.werkUrl || (werkUrl ? `${String(werkUrl).trim().replace(/\/$/, '')}/odoo/products/${productId}` : null);
                    const primaryUrl = werkProductUrl || odooUrl;
                    const isPrimaryReal = primaryUrl && String(primaryUrl).startsWith('http');
                    const isOdooReal = odooUrl && String(odooUrl).startsWith('http');
                    if (productId == null) return null;
                    return (
                      <div style={{ marginTop: 12 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Open in Odoo:</span>
                        {isPrimaryReal ? (
                          <a
                            href={primaryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'block', marginTop: 4, fontSize: 13, wordBreak: 'break-all', color: '#6A1B9A' }}
                          >
                            {primaryUrl}
                          </a>
                        ) : (
                          <span style={{ display: 'block', marginTop: 4, fontSize: 13, wordBreak: 'break-all' }}>
                            Product-id: {productId}
                          </span>
                        )}
                        {isOdooReal && odooUrl !== primaryUrl && (
                          <div style={{ marginTop: 6 }}>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>Odoo API-link: </span>
                            <span style={{ fontSize: 11, color: '#9ca3af', wordBreak: 'break-all' }}>{odooUrl}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: 20, padding: 16, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#92400e' }}>
                      Zichtbaarheid bijlage aanpassen in Odoo
                    </p>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
                      De PDF-bijlage staat standaard op <strong>"Verborgen"</strong>. Pas de zichtbaarheid aan naar <strong>"Binnen de pdf van de offerte"</strong> zodat de bijlage zichtbaar is in de Offertebouwer.
                    </p>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <p style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginBottom: 6 }}>1. Standaard: Verborgen</p>
                        <img src="/demo/odoo-bijlage-verborgen.png" alt="Bijlage staat op Verborgen" style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }} />
                      </div>
                      <div style={{ flex: '1 1 200px' }}>
                        <p style={{ fontSize: 12, color: '#065f46', fontWeight: 600, marginBottom: 6 }}>2. Aanpassen naar: Binnen de pdf van de offerte</p>
                        <img src="/demo/odoo-bijlage-binnen-offerte.png" alt="Bijlage op Binnen de pdf van de offerte" style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card" style={{ background: '#fef2f2', border: '1px solid #ef4444', padding: 16 }}>
                  <div className="error">{offertebijlageResult.error || 'Mislukt'}</div>
                </div>
              )}
            </div>
          )}

          <hr style={{ margin: '20px 0', borderColor: '#e5e7eb' }} />
          {finalizeError && <div className="error" style={{ marginBottom: 12 }}>{finalizeError}</div>}
          {!offertebijlageResult?.success && (
            <div className="info-message" style={{ marginBottom: 12, padding: 12, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, color: '#92400e' }}>
              <p style={{ margin: '0 0 8px 0' }}>⚠️ Maak eerst het Offertebijlage-product aan (knop 2) voordat je kunt afronden.</p>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>Door af te ronden worden de gegevens naar Odoo gestuurd, aangemaakt en de verkoopkans en offerte aangemaakt. Wilt u een extra offerte in een bestaande verkoopkans? Voeg de pdf handmatig toe aan een offerte die u in de verkoopkans aanmaakt. Rond dan niet af.</p>
            </div>
          )}

          {existingOdooQuotationId ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onComplete({ synced: false, usedExisting: true });
                }}
                disabled={finalizing || !offertebijlageResult?.success}
              >
                Afronden met bestaande offerte
              </button>
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 2 }}>
                Er bestaat al een Odoo-offerte (ID: {existingOdooQuotationId}
                {existingOdooOpportunityId ? `, verkoopkans: ${existingOdooOpportunityId}` : ''}).
                Hiermee rondt u af zonder een nieuwe offerte in Odoo aan te maken.
              </span>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  setFinalizing(true);
                  setFinalizeError(null);
                  try {
                    const response = await api.post(`/api/quotations/${quotationId}/sync-odoo`);
                    onComplete({ synced: true });
                  } catch (err) {
                    const apiError = err.response?.data?.error || err.response?.data?.message || 'Synchronisatie naar Odoo mislukt';
                    setFinalizeError(apiError);
                  } finally {
                    setFinalizing(false);
                  }
                }}
                disabled={finalizing || !offertebijlageResult?.success}
              >
                {finalizing ? 'Afronden...' : 'Afronden en nieuwe offerte maken in Odoo'}
              </button>
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 2 }}>
                Maakt een nieuwe verkoopkans en offerte aan in Odoo (naast de bestaande).
              </span>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                setFinalizing(true);
                setFinalizeError(null);
                try {
                  const response = await api.post(`/api/quotations/${quotationId}/sync-odoo`);
                  onComplete({ synced: true });
                } catch (err) {
                  const apiError = err.response?.data?.error || err.response?.data?.message || 'Synchronisatie naar Odoo mislukt';
                  setFinalizeError(apiError);
                } finally {
                  setFinalizing(false);
                }
              }}
              disabled={finalizing || !offertebijlageResult?.success}
              style={{ marginTop: 8 }}
            >
              {finalizing ? 'Afronden...' : 'Afronden'}
            </button>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Completion Step
const CompletionStep = ({ usedExisting, onComplete }) => {
  return (
    <div className="wizard-step" data-demo-step="demo-step-7">
      <div className="container">
        <h2>✅ Offerte Gereed</h2>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          {usedExisting ? (
            <>
              <h3 style={{ color: '#10b981', marginBottom: 20 }}>Bestaande offertebijlage</h3>
              <p style={{ fontSize: 16, marginBottom: 12, color: '#374151' }}>
                De actuele PDF staat in Odoo na <strong>PDF genereren</strong> (stap 6). Daarna uploadt de server die één keer naar het Offertebijlage-product.
              </p>
              <p style={{ fontSize: 14, marginBottom: 24, color: '#6b7280' }}>
                Verstuur de offerte vanuit <strong>Odoo</strong> met de juiste offertebrief.
              </p>
            </>
          ) : (
            <>
              <h3 style={{ color: '#10b981', marginBottom: 20 }}>Nieuwe offerte aangemaakt!</h3>
              <p style={{ fontSize: 16, marginBottom: 12, color: '#374151' }}>
                De offerte is aangemaakt en gesynchroniseerd met Odoo.
              </p>
              <p style={{ fontSize: 14, marginBottom: 24, color: '#6b7280' }}>
                Controleer en verstuur de offerte vanuit <strong>Odoo</strong> met de juiste offertebrief.
              </p>
            </>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (onComplete) onComplete();
            }}
            style={{ fontSize: 16, padding: '12px 24px' }}
          >
            Naar Hoofdscherm
          </button>
        </div>
      </div>
    </div>
  );
};

// Data Fetch Step Component
const DataFetchStep = ({ clientName, query, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedData, setFetchedData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch from Odoo and GPT in parallel
      const [odooData, gptData] = await Promise.all([
        fetchClientFromOdoo(clientName),
        fetchFromGPT(query)
      ]);

      setFetchedData({
        clientData: odooData,
        gptData: gptData
      });

      onComplete({
        clientData: odooData?.clientData || '',
        gptData: gptData
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientFromOdoo = async (name) => {
    try {
      const response = await api.post('/api/odoo/search-client', { clientName: name });
      return response.data.found ? response.data.clientData : null;
    } catch (error) {
      return null;
    }
  };

  const fetchFromGPT = async (query) => {
    if (!query) return null;
    try {
      const response = await api.post('/api/offertegpt/query', { query });
      return response.data;
    } catch (error) {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="wizard-step">
        <div className="container">
          <h2>Stap 4: Data Ophalen</h2>
          <div className="card">
            <div className="loading">Gegevens ophalen van Odoo en GPT...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wizard-step">
        <div className="container">
          <h2>Stap 4: Data Ophalen</h2>
          <div className="card">
            <div className="error">{error}</div>
            <button className="btn btn-primary" onClick={fetchData}>
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="container">
        <h2>Stap 4: Data Opgehaald</h2>
        <div className="card">
          <div className="success">
            ✓ Klantgegevens opgehaald uit Odoo
            {fetchedData?.gptData && <br />}
            {fetchedData?.gptData && '✓ GPT informatie verwerkt'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationWizard;
